import React, { useState } from "react";
import {
  HStack,
  VStack,
  IconButton,
  Input,
  Text,
  Tag,
  TagLabel,
  TagCloseButton,
  FormControl,
  FormLabel,
  FormHelperText,
  InputGroup,
} from "@chakra-ui/react";
import { AddIcon, ChevronUpIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { useFormikContext } from "formik";

interface AllowedValuesBuilderProps {
  name: string;
  label: string;
  type: "string" | "number";
  placeholder?: string;
  helperText?: string;
}

export const AllowedValuesBuilder: React.FC<AllowedValuesBuilderProps> = ({
  name,
  label,
  type,
  placeholder,
  helperText,
}) => {
  const { values, setFieldValue } = useFormikContext<any>();
  const [newValue, setNewValue] = useState("");

  // Parse the comma-separated string from formik
  const currentValue = values[name] || "";
  const items = currentValue
    ? currentValue
        .split(",")
        .map((v: string) => v.trim())
        .filter((v: string) => v)
    : [];

  const addItem = () => {
    if (!newValue.trim()) return;

    // Validate for number type
    if (type === "number") {
      const num = parseFloat(newValue);
      if (isNaN(num)) {
        return; // Invalid number
      }
    }

    const updatedItems = [...items, newValue.trim()];
    setFieldValue(name, updatedItems.join(", "));
    setNewValue("");
  };

  const removeItem = (index: number) => {
    const updatedItems = items.filter((_: any, i: number) => i !== index);
    setFieldValue(name, updatedItems.join(", "));
  };

  const moveItemUp = (index: number) => {
    if (index === 0) return;
    const updatedItems = [...items];
    [updatedItems[index - 1], updatedItems[index]] = [
      updatedItems[index],
      updatedItems[index - 1],
    ];
    setFieldValue(name, updatedItems.join(", "));
  };

  const moveItemDown = (index: number) => {
    if (index === items.length - 1) return;
    const updatedItems = [...items];
    [updatedItems[index], updatedItems[index + 1]] = [
      updatedItems[index + 1],
      updatedItems[index],
    ];
    setFieldValue(name, updatedItems.join(", "));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addItem();
    }
  };

  return (
    <FormControl display="flex" flexDir="row">
      <VStack w="100%" alignItems="flex-start" flex={1} pt={2}>
        <FormLabel fontWeight="bold" color="cherryGrey.900">
          {label}
        </FormLabel>
        {helperText && (
          <FormHelperText marginBottom="2">{helperText}</FormHelperText>
        )}
      </VStack>
      <VStack w="100%" alignItems="flex-start" alignSelf="flex-start" flex={1}>
        <HStack w="100%">
          <InputGroup shadow="sm" flex={1}>
            <Input
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={
                placeholder ||
                `Add ${type === "number" ? "number" : "value"}...`
              }
              type={type === "number" ? "text" : "text"}
              backgroundColor="white"
            />
          </InputGroup>
          <IconButton
            aria-label="Add value"
            icon={<AddIcon />}
            size="md"
            colorScheme="cherryButton"
            onClick={addItem}
            isDisabled={!newValue.trim()}
          />
        </HStack>

        {items.length > 0 && (
          <VStack
            w="100%"
            alignItems="stretch"
            spacing={1}
            maxH="200px"
            overflowY="auto"
            p={2}
            borderWidth={1}
            borderRadius="md"
            backgroundColor="white"
          >
            {items.map((item: string, index: number) => (
              <HStack key={index} justifyContent="space-between">
                <Tag size="md" variant="subtle" colorScheme="blue" flex={1}>
                  <TagLabel>{item}</TagLabel>
                  <TagCloseButton onClick={() => removeItem(index)} />
                </Tag>
                <HStack spacing={0}>
                  <IconButton
                    aria-label="Move up"
                    icon={<ChevronUpIcon />}
                    size="xs"
                    variant="ghost"
                    onClick={() => moveItemUp(index)}
                    isDisabled={index === 0}
                  />
                  <IconButton
                    aria-label="Move down"
                    icon={<ChevronDownIcon />}
                    size="xs"
                    variant="ghost"
                    onClick={() => moveItemDown(index)}
                    isDisabled={index === items.length - 1}
                  />
                </HStack>
              </HStack>
            ))}
          </VStack>
        )}

        {items.length === 0 && (
          <Text fontSize="sm" color="gray.400" fontStyle="italic">
            No values added yet
          </Text>
        )}
      </VStack>
    </FormControl>
  );
};
